import { useQuery } from "@apollo/client";
import React from "react";
import { useIntl } from "react-intl";
import AddPercent from "./AddPercent";
import { OnboardingInfo } from "../../graphql/onboarding/onboarding/types/OnboardingInfo";
import { ONBOARDING } from "../../graphql/onboarding/onboarding/query";
import { DOCUMENTS_TYPE } from "../../graphql/onboarding/document/query";
import { DocumentType } from "../../enums/document";
import {
  DocumentsType,
  DocumentsTypeVariables,
} from "../../graphql/onboarding/document/types/DocumentsType";
import {
  CreateMemberInput,
  CreateUserInput,
  InputDocument,
} from "../../types/graphql-global-types-onboarding";
import { mappedMember } from "./logic";
import { useNavigate } from "react-router-dom";
import { COMPANY_INFO_PAPPERS } from "../../graphql/onboarding/papers/query";
import {
  FetchCompanyPappers,
  FetchCompanyPappers_fetchCompanyPappers_ubo,
  FetchCompanyPappersVariables,
} from "../../graphql/onboarding/papers/types/FetchCompanyPappers";
import { DocumentInfo } from "../../graphql/onboarding/document/types/DocumentInfo";
import StatusGetID from "../GetId/statusGetId";
import Layout from "../Common/NewLayout";
import Header from "../Common/NewLayout/Header";
import { useToastNotifications } from "../../utils/toastUtils";

const AddPercentProvider = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  let shares: CreateUserInput[] = [];
  let document: InputDocument | null = null;

  const { showErrorToast } = useToastNotifications();
  const { data: companyPappers, loading: pappersLoading } = useQuery<
    FetchCompanyPappers,
    FetchCompanyPappersVariables
  >(COMPANY_INFO_PAPPERS, {
    fetchPolicy: "no-cache",
    variables: {
      documentTypes: [DocumentType.UBO_REGISTRY],
    },
  });

  const { active, loading: loadingGetId } = StatusGetID();

  const {
    data: onboarding,
    loading,
    refetch,
  } = useQuery<OnboardingInfo>(ONBOARDING, {
    fetchPolicy: "network-only",
    onError: () => {
      showErrorToast({ description: "stepSix.error.loadShareholderError" });
    },
  });

  const { data: typesDocument, loading: loadingDocumentsType } = useQuery<
    DocumentsType,
    DocumentsTypeVariables
  >(DOCUMENTS_TYPE, {
    fetchPolicy: "network-only",
    variables: {
      types: [
        DocumentType.UBO_REGISTRY,
        DocumentType.IDENTITY_CARD,
        DocumentType.PASSPORT,
        DocumentType.DRIVING_LICENCE,
        DocumentType.PROOF_OF_ADDRESS,
      ],
    },
  });

  if (onboarding?.onboardingInfo) {
    const {
      users,
      legalRepresentative: owner,
      company,
    } = onboarding.onboardingInfo;
    const ubo = company?.isAutocomplete
      ? companyPappers?.fetchCompanyPappers?.ubo ?? []
      : [];
    const representatives = company?.isAutocomplete
      ? companyPappers?.fetchCompanyPappers?.representatives ?? []
      : [];

    const uniqueShares = new Set<string>(); // Pour éviter les doublons
    const addMember = (
      member: FetchCompanyPappers_fetchCompanyPappers_ubo | null,
    ) => {
      if (member) {
        const fullName = `${member?.firstname || ""} ${member.name || ""}`;
        if (!uniqueShares.has(fullName)) {
          uniqueShares.add(fullName);
          shares.push(mappedMember(member));
        }
      }
    };

    // Ajouter le propriétaire
    if (owner?.id) addMember(owner);

    // Ajouter les membres internes s'ils existent
    users?.forEach((m) => m && addMember(m));

    // Fusionner UBO et Directeurs en s'assurant qu'ils ne sont pas `null`
    [...(ubo ?? []), ...(representatives ?? [])]
      .filter(
        (u) => u && u.firstname !== owner?.firstname && u.name !== owner?.name,
      )
      .forEach(addMember);

    const findUBORegistryDocument = (documents: (DocumentInfo | null)[]) =>
      documents?.find((d) => d?.type?.type === DocumentType.UBO_REGISTRY);
    let doc;

    if (company?.documents?.length) {
      doc = findUBORegistryDocument(company?.documents);
    }
    if (
      company?.isAutocomplete &&
      companyPappers?.fetchCompanyPappers?.company?.documents?.length
    ) {
      doc = findUBORegistryDocument(
        companyPappers?.fetchCompanyPappers?.company?.documents,
      );
    }

    if (doc) {
      document = {
        base64Content: doc.base64Content,
        filename: doc.filename,
        fileType: doc.fileType,
        type: doc.type?.type,
      };
    }
  }

  if (!!onboarding?.onboardingInfo?.company?.isIndividual) {
    navigate("/identity-verification");
  }

  return (
    <Layout
      title="Actionnaires"
      description="Complétez votre dossier en indiquant les informations sur les personnes actionnaires de votre entreprise."
      isDoubleContent
      loading={
        loading || loadingDocumentsType || pappersLoading || loadingGetId
      }
    >
      <Header
        title={intl.formatMessage({
          id: `addPercent.title`,
        })}
        description={intl.formatMessage({
          id: `addPercent.paragraph2`,
        })}
      />
      <AddPercent
        shares={shares}
        refetch={refetch}
        isIndividual={!!onboarding?.onboardingInfo?.company?.isIndividual}
        documentsType={typesDocument?.documentsType ?? []}
        document={document}
        previous={"/manager"}
        next={active ? "/identity-verification" : "/contrat"}
        activeGetId={active}
      />
    </Layout>
  );
};

export default AddPercentProvider;
